// src/pages/providers/2_dashboard/components/PatientRemoveForm.js
import React, { useState, useContext } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { ExclamationIcon } from '@heroicons/react/solid';
import { auth, functions } from '../../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { GlobalStateContext } from '../../../../context/GlobalStateContext';
import { toast } from 'react-toastify';

function PatientRemoveForm({ isOpen, onClose, patient }) {
    const { optimisticRemovePatient, refreshPatientsData } = useContext(GlobalStateContext);
    const [deleteText, setDeleteText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    /**
     * UX safety: prevent the user from dismissing the dialog while the delete operation
     * is in-flight.
     *
     * Why:
     * - The backend deletion (callable function) cannot be cancelled once started.
     * - Allowing the dialog to close mid-flight can confuse the user (did it work?),
     *   and increases the chance they click delete twice or navigate away.
     * - This matches the "disable Cancel while Archiving..." pattern used elsewhere.
     */
    const handleClose = React.useCallback(() => {
        if (!isDeleting) {
            onClose();
        }
    }, [isDeleting, onClose]);

    // Reset states when dialog opens
    React.useEffect(() => {
        if (isOpen) {
            setDeleteText('');
            setIsDeleting(false);
            setError(null);
            setSuccess(false);
        }
    }, [isOpen]);

    const handleDeletePatient = async () => {
        if (deleteText !== 'DELETE' || !patient) return;

        setIsDeleting(true);
        setError(null);

        try {
            // removePatient is the dashboard "Delete Patient" action. The old
            // callable name was archivePatient; that is now a server-side alias
            // only, kept for deploy-time safety.
            const removePatientFn = httpsCallable(functions, 'removePatient');
            await removePatientFn({
                patientId: patient.patientId,
                reason: `${auth.currentUser?.displayName || 'Admin'} requested deletion via dashboard`
            });

            // Optimistic (post-success) UI update:
            // Remove the row immediately without waiting for the refetch.
            // No force-refresh here — the optimistic state IS the truth (the CF succeeded).
            // The next natural SWR cycle (30s TTL) will confirm with the server.
            // Force-refreshing immediately would overwrite the optimistic state with stale
            // data from patient_summaries (trigger may not have updated the summary yet).
            try {
                optimisticRemovePatient(patient.patientId);
            } catch (e) {}

            toast.success(`Patient ${patient?.firstName || ''} ${patient?.lastName || ''} has been deleted`.trim());

            setSuccess(true);

            // Auto close after success
            setTimeout(() => {
                onClose();
            }, 1000);
        } catch (err) {
            console.error('Error removing patient:', err);
            setError(err.message || 'An error occurred while trying to delete the patient.');
            // Ensure list is consistent even if we failed mid-flight
            try { await refreshPatientsData({ force: true }); } catch (e) {}
        } finally {
            setIsDeleting(false);
        }
    };

    const isDeleteEnabled = deleteText === 'DELETE' && !isDeleting;

    return (
        <AlertDialog.Root
            open={isOpen}
            // Accessibility/UX: only run the close callback when the dialog is actually closing.
            // This prevents accidental closes (and loss of typed input) during unrelated re-renders.
            onOpenChange={(open) => {
                // Prevent overlay/escape dismissal while deleting.
                if (!open) handleClose();
            }}
        >
            <AlertDialog.Portal>
                <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm" />
                <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg p-6 max-w-md w-[90vw] max-h-[85vh] overflow-auto">
                    <AlertDialog.Title className="text-xl font-semibold text-gray-900 flex items-center">
                        <ExclamationIcon className="w-6 h-6 text-red-500 mr-2" />
                        Delete Patient
                    </AlertDialog.Title>


                    <div className="mt-3 text-sm text-gray-700">
                        <AlertDialog.Description asChild>
                            <p className="mb-2">
                                You are about to permanently remove:
                            </p>
                        </AlertDialog.Description>

                        <p className="font-medium text-gray-900 mb-4">
                            {patient?.firstName} {patient?.lastName} ({patient?.email})
                        </p>

                        <div className="bg-red-50 rounded-lg p-4 mb-4">
                            <div className="flex items-start">
                                <div className="">
                                    <p className="text-sm text-red-700">
                                        This action cannot be undone. The patient's records will be removed from view and their account will be disabled so they will no longer be able to log in.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-900 mb-4">
                                Type DELETE in all caps to confirm:
                            </label>
                            <input
                                type="text"
                                value={deleteText}
                                onChange={(e) => setDeleteText(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                                placeholder="Type DELETE here"
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="mt-4 bg-red-50 border-l-4 border-red-400 p-4">
                            <div className="flex">
                                <div className="ml-3">
                                    <p className="text-sm text-red-700">
                                        {error}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {success && (
                        <div className="mt-4 bg-green-50 border-l-4 border-green-400 p-4">
                            <div className="flex">
                                <div className="ml-3">
                                    <p className="text-sm text-green-700">
                                        Patient record successfully deleted.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mt-6 flex justify-end gap-3">
                        <AlertDialog.Cancel asChild>
                            <button
                                onClick={handleClose}
                                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                        </AlertDialog.Cancel>

                        <button
                            onClick={handleDeletePatient}
                            disabled={!isDeleteEnabled}
                            className={`px-4 py-2 text-sm font-semibold text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${isDeleteEnabled
                                    ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                                    : 'bg-red-300 cursor-not-allowed'
                                }`}
                        >
                            {isDeleting ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Deleting...
                                </span>
                            ) : (
                                'Delete Patient'
                            )}
                        </button>
                    </div>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
}

export default PatientRemoveForm;